import {
  type ChangeEvent,
  type FocusEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import styles from "./NumberInput.module.css";

export interface NumberInputProps {
  value: number | null;
  onChange: (value: number | null) => void;
  label?: string;
  evaluateExpression?: boolean;
  disabled?: boolean;
  id?: string;
  "data-testid"?: string;
}

const HINT_TEXT = "Du kan regne, f.eks. 10+5";
const ERROR_TEXT = "Ugyldig uttrykk";

export function NumberInput({
  value,
  onChange,
  label,
  evaluateExpression = false,
  disabled,
  id,
  "data-testid": testId,
}: NumberInputProps) {
  const autoId = useId();
  const inputId = id ?? autoId;

  const [buffer, setBuffer] = useState<string>(() => formatNumber(value));
  const [hasError, setHasError] = useState(false);
  const lastEmittedRef = useRef<number | null>(value);

  // Resync from external value changes (but not from our own emissions).
  useEffect(() => {
    if (value !== lastEmittedRef.current) {
      setBuffer(formatNumber(value));
      setHasError(false);
      lastEmittedRef.current = value;
    }
  }, [value]);

  function emit(next: number | null) {
    lastEmittedRef.current = next;
    onChange(next);
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const input = event.target;
    const raw = input.value;
    const dotCount = countDots(raw, input.selectionStart ?? raw.length);
    const normalized = raw.replace(/\./g, ",");
    setBuffer(normalized);
    setHasError(false);
    // Restore caret position (`.` and `,` are same width — same offset).
    if (dotCount > 0) {
      const pos = input.selectionStart;
      if (pos !== null) {
        queueMicrotask(() => {
          try {
            input.setSelectionRange(pos, pos);
          } catch {
            // Ignored — happens if the input is no longer focused.
          }
        });
      }
    }
    if (evaluateExpression && containsOperator(normalized)) {
      // Don't try to parse mid-expression — wait for blur.
      emit(null);
      return;
    }
    const parsed = parseSingleNumber(normalized);
    emit(parsed);
  }

  function handleBlur(_event: FocusEvent<HTMLInputElement>) {
    const trimmed = buffer.trim();
    if (trimmed === "") {
      setHasError(false);
      emit(null);
      return;
    }
    if (evaluateExpression && containsOperator(trimmed)) {
      const result = evaluateAdditiveExpression(trimmed);
      if (result === null) {
        setHasError(true);
        emit(null);
        return;
      }
      const scrubbed = scrubFloat(result);
      setBuffer(formatNumber(scrubbed));
      setHasError(false);
      emit(scrubbed);
      return;
    }
    const parsed = parseSingleNumber(trimmed);
    if (parsed === null) {
      setHasError(true);
      emit(null);
      return;
    }
    const scrubbed = scrubFloat(parsed);
    setBuffer(formatNumber(scrubbed));
    setHasError(false);
    emit(scrubbed);
  }

  const inputMode = evaluateExpression ? "text" : "decimal";
  const hintId = `${inputId}-hint`;
  const showError = hasError;
  const showHint = evaluateExpression && !showError;

  return (
    <div className={styles.field}>
      {label !== undefined && (
        <label className={styles.label} htmlFor={inputId}>
          {label}
        </label>
      )}
      <input
        id={inputId}
        data-testid={testId}
        className={styles.input}
        type="text"
        inputMode={inputMode}
        autoComplete="off"
        disabled={disabled}
        value={buffer}
        onChange={handleChange}
        onBlur={handleBlur}
        aria-invalid={showError || undefined}
        aria-describedby={showHint || showError ? hintId : undefined}
      />
      {(showHint || showError) && (
        <div
          id={hintId}
          className={`${styles.hint} ${showError ? styles.error : ""}`.trim()}
          data-testid={testId ? `${testId}-hint` : undefined}
        >
          {showError ? ERROR_TEXT : HINT_TEXT}
        </div>
      )}
    </div>
  );
}

function formatNumber(value: number | null): string {
  if (value === null) return "";
  return String(value).replace(".", ",");
}

function countDots(s: string, upTo: number): number {
  let n = 0;
  for (let i = 0; i < upTo && i < s.length; i++) {
    if (s[i] === ".") n++;
  }
  return n;
}

function containsOperator(s: string): boolean {
  // A leading "-" is a sign, not a binary operator.
  for (let i = 1; i < s.length; i++) {
    if (s[i] === "+" || s[i] === "-") return true;
  }
  return false;
}

function parseSingleNumber(s: string): number | null {
  if (s === "" || s === "-" || s === "," || s === "-,") return null;
  // Reject anything that isn't optional sign + digits + optional comma + digits.
  if (!/^-?\d*,?\d*$/.test(s)) return null;
  const normalized = s.replace(",", ".");
  const n = Number.parseFloat(normalized);
  return Number.isFinite(n) ? n : null;
}

function evaluateAdditiveExpression(s: string): number | null {
  // Tokenize: numbers separated by + or -. Allow a leading sign.
  const cleaned = s.replace(/\s+/g, "");
  if (cleaned === "") return null;
  // Split keeping operators. A leading "-" is treated as sign on the first term.
  const tokens: string[] = [];
  let current = "";
  for (let i = 0; i < cleaned.length; i++) {
    const ch = cleaned[i];
    if ((ch === "+" || ch === "-") && i > 0) {
      if (current === "") return null;
      tokens.push(current, ch);
      current = "";
    } else {
      current += ch;
    }
  }
  if (current === "") return null;
  tokens.push(current);
  // Now tokens alternate: number, op, number, op, ...
  let total = parseSingleNumber(tokens[0]);
  if (total === null) return null;
  for (let i = 1; i < tokens.length; i += 2) {
    const op = tokens[i];
    const next = parseSingleNumber(tokens[i + 1] ?? "");
    if (next === null) return null;
    if (op === "+") total += next;
    else if (op === "-") total -= next;
    else return null;
  }
  return Number.isFinite(total) ? total : null;
}

function scrubFloat(n: number): number {
  return Number(n.toPrecision(12));
}
